/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { FocusTrap } from './focus_trap';

/**
 * Turn a navigation menu list into a custom dropdown
 */
export class CustomNavSelect {
    nav: HTMLElement;
    parent: HTMLElement;
    container: HTMLElement;
    triggerContainer: HTMLElement;
    items: HTMLAnchorElement[];
    current: HTMLElement | null = null;
    trigger: HTMLElement | null = null;
    isOpen: boolean = false;
    height: number;
    icon: string = '<i class="fa fa-angle-down" aria-hidden="true"></i>';
    isSet: boolean = false;
    trap: FocusTrap;

    constructor(nav: HTMLElement, parent: HTMLElement) {
        this.nav = nav;
        this.parent = parent;
        this.container = parent.querySelector('.inner');
        this.items = Array.from(this.nav.querySelectorAll('a'));
        this.trap = new FocusTrap(this.parent);
    }

    public unset(): void {
        if (this.isSet) {
            this.parent.classList.remove('anchor-menu--dropdown');

            this.removeListeners();
            if (this.trigger && this.triggerContainer) {
                this.triggerContainer.remove();
                this.trigger = null;
            }
            if (this.nav) {
                this.container.removeAttribute('style');
                this.container.removeAttribute('aria-expanded');
            }
            this.isSet = false;
            this.isOpen = false;
        }
    }

    public set(): void {
        if (!this.isSet) {
            if (!this.nav.parentNode || this.items.length < 1) {
                return;
            }

            this.parent.classList.add('anchor-menu--dropdown');
            this.buildItemList();
            this.buildTrigger();
            this.setHeight();
            this.attachListeners();
            this.isSet = true;
        }
    }

    public setCurrent(item: HTMLAnchorElement): void {
        this.current = item;
    }

    public open(): void {
        this.parent.classList.add('is-open');
        this.trigger?.setAttribute('aria-expanded', 'true');
        this.container.setAttribute('aria-expanded', 'true');
        this.container.style.height = `${this.height}px`;
        this.isOpen = true;
        this.trap.setTrap();
    }

    public close(): void {
        this.parent.classList.remove('is-open');
        this.trigger?.setAttribute('aria-expanded', 'false');
        this.container.setAttribute('aria-expanded', 'false');
        this.container.style.height = '0px';
        this.isOpen = false;
        this.trap.releaseTrap();
    }

    private buildItemList(): void {
        this.parent.classList.add('anchor-menu--dropdown');
        this.current = this.items.length > 0 ? this.items[0] : null;
    }

    private buildTrigger(): void {
        if (this.trigger) {
            return;
        }
        const text = this.nav.getAttribute('data-trigger-label');
        this.triggerContainer = document.createElement('div');
        this.triggerContainer.classList.add('container');
        this.container.setAttribute('aria-expanded', 'false');
        this.container.id = 'anchor-nav-container';
        this.trigger = document.createElement('button');
        this.trigger.innerHTML = text ? `${this.icon} ${text}` : `${this.icon} Jump to section`;
        this.trigger.classList.add('anchor-menu__trigger');
        this.trigger.setAttribute('aria-controls', '#anchor-nav');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.setAttribute('aria-controls', this.container.id);
        this.parent.insertBefore(this.triggerContainer, this.parent.firstChild);
        this.triggerContainer.insertBefore(this.trigger, this.triggerContainer.firstChild);
    }

    private setHeight(): void {
        this.height = this.nav.offsetHeight;
        this.container.style.height = '0px';
    }

    private attachListeners(): void {
        this.trigger?.addEventListener('click', (e) => this.toggleState(e));
        this.trigger?.addEventListener('keyup', (e) => this.closeViaKeyboard(e));
        this.container.addEventListener('keyup', (e) => this.closeViaKeyboard(e));
    }

    private removeListeners(): void {
        this.trigger?.removeEventListener('click', (e) => this.toggleState(e));
        this.trigger?.removeEventListener('keyup', (e) => this.closeViaKeyboard(e));
        this.container.removeEventListener('keyup', (e) => this.closeViaKeyboard(e));
    }

    private closeViaKeyboard(e: KeyboardEvent): void {
        if (e.key === 'Escape') {
            this.trigger?.focus();
            this.close();
        }
    }

    private toggleState(e: Event): void {
        e.preventDefault();
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
        e.stopPropagation();
    }
}
